import { useState } from 'react'

import { type FinancialAccountTypeEnum } from '@webapp/platform/banking/entities/enums/FinancialAccountTypeEnum'
import { type PaymentMethodCategoryType } from '@webapp/platform/banking/entities/enums/PaymentMethodCategoryEnum'
import {
  type PaymentMethodCategoryOption,
  useSelectPaymentMethodCategoryOptions,
} from './useSelectPaymentMethodCategoryOptions'

export interface OnSubmitPayload {
  paymentMethodCategoryType: PaymentMethodCategoryType
}

export interface IUseSelectPaymentMethodCategoryStepProps {
  financialAccountType: Nullable<FinancialAccountTypeEnum>
  isFinancialAccountSetUp: boolean
  onSubmit: (data: OnSubmitPayload) => void
  onCancel: () => void
}

export interface IUseSelectPaymentMethodCategoryStepResult {
  selectedPaymentMethod: PaymentMethodCategoryType | null
  paymentMethodCategoryOptions: PaymentMethodCategoryOption[]
  handleMethodSelect: (method: PaymentMethodCategoryType) => void
  handleContinue: () => void
  handleCancel: () => void
  canContinue: boolean
}

export const useSelectPaymentMethodCategoryStep = ({
  financialAccountType,
  onSubmit,
  onCancel,
}: IUseSelectPaymentMethodCategoryStepProps): IUseSelectPaymentMethodCategoryStepResult => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodCategoryType | null>(null)

  const { paymentMethodCategoryOptions } =
    useSelectPaymentMethodCategoryOptions({
      financialAccountType,
    })

  const handleMethodSelect = (method: PaymentMethodCategoryType) => {
    setSelectedPaymentMethod(method)
  }

  const handleContinue = () => {
    if (selectedPaymentMethod) {
      onSubmit({
        paymentMethodCategoryType: selectedPaymentMethod,
      })
    }
  }

  const handleCancel = () => {
    setSelectedPaymentMethod(null)
    onCancel()
  }

  // Continue button is enabled as soon as a payment method is selected
  const canContinue = selectedPaymentMethod !== null

  return {
    selectedPaymentMethod,
    paymentMethodCategoryOptions,
    handleMethodSelect,
    handleContinue,
    handleCancel,
    canContinue,
  }
}
